import React, { useState } from "react"
import { normalizeDestinationUrl, validateDestinationUrl, mailtoSuggestion } from "../lib/urls"
import { validateEmail } from "../lib/email"
import ReCheckLink from "./ReCheckLink"
import MailtoSuggestion from "./MailtoSuggestion"
import DestinationInput from "./DestinationInput"
import ContactPageFields, {
  EMPTY_CONTACT_PAGE, cleanContactPage, validateContactPage, contactPageErrorsFromServer
} from "./ContactPageFields"

// Server-error `field` values this form has somewhere to render. Anything else
// falls through to the URL slot rather than being dropped on the floor.
const HANDLED_SERVER_FIELDS = [ "email", "form", "contact_page" ]

// QrCode::LABEL_MAX_LENGTH. The field stops there, so the server never has to
// refuse a name.
const NAME_MAX_LENGTH = 80

function clientValidate(value) {
  if (!value.trim() || /^mailto:\s*$/i.test(value)) {
    return /^mailto:/i.test(value)
      ? "Enter the email address the code should write to."
      : "We need a URL. Paste one above to get started."
  }
  return validateDestinationUrl(value).error
}

// Destination, email and the dashboard's Look are the same control; only the
// error border forks.
// CLAUDE.md pins the shape (border token, voltage focus ring at 2px offset,
// rounded 12px), so keep one definition rather than two that drift apart the
// next time a brand token moves.
export const fieldClasses = (hasError) =>
  "w-full px-4 py-3 rounded border bg-white dark:bg-midnight text-ink dark:text-cloud text-base placeholder:text-[color:var(--color-muted)] dark:placeholder:text-muted-light " +
  "focus:outline-none focus:ring-2 focus:ring-voltage focus:border-voltage " +
  "disabled:opacity-60 disabled:cursor-not-allowed " +
  (hasError
    ? "border-[color:var(--color-fail)]"
    : "border-[color:var(--color-border)] dark:border-storm")

// `requireEmail` turns the builder into a one-step form: destination + email,
// one submit. It's driven by the EmailGate flag through a data attribute on the
// mount point, so a signed-in visitor (whose address we already know) and the
// flag-off arm never see the field. See docs/specs/2026-08-12-email-gate-on-create.md.
// `verifying` is true while the browser check runs with no token yet: on page
// load until the first one arrives (#339), and while a challenge is re-issued
// after a refusal (#166). The button says "Checking your browser…" and still
// takes a press; the caller holds it (useBotProtection's submitWhenReady) and
// sends it when the check settles and the form is old enough for the server's
// timing check. `held` is that held press: the button and
// fields lock, so the press goes once and sends what was on screen. Both are
// distinct from `disabled` so the button says what's actually happening.
//
// `formId` + `hideSubmit` let a caller put the submit button elsewhere (the
// dashboard creator's finish bar, #316) with `form={formId}`; Enter in the
// field still submits.
//
// `destinationPlaceholder` swaps the Website example link for the host page's
// own (#340); unset, the kind's default shows.
//
// `contactPages` offers the hosted Contact page kind (#332): the URL field
// gives way to the page's fields, and the submit hands them over as the
// fourth argument with no destination. `initialPage` opens the form on that
// kind with the fields filled (the vCard tool's hand-over). The email gate and
// its opt-out line do not move or change.
export default function UrlForm({ onSubmit, disabled, verifying = false, held = false, serverError, layout = "hero", requireEmail = false, formId, hideSubmit = false, destinationPlaceholder, contactPages = false, initialPage = null }) {
  const [value, setValue] = useState("")
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [hosted, setHosted] = useState(Boolean(contactPages && initialPage))
  const [page, setPage] = useState(() => ({ ...EMPTY_CONTACT_PAGE, ...(initialPage || {}) }))
  const [pageError, setPageError] = useState(null)
  // Only validate once the user has actually tried to submit. Showing "We need
  // a URL" before any interaction (or just on blur) reads as the form yelling
  // at someone who hasn't done anything wrong yet.
  const [submitted, setSubmitted] = useState(false)
  const [clientError, setClientError] = useState(null)
  const [emailError, setEmailError] = useState(null)

  const handleSubmit = (e) => {
    e.preventDefault()
    const emailErr = requireEmail ? validateEmail(email) : null
    setSubmitted(true)
    setEmailError(emailErr)
    if (hosted) {
      const pageErr = validateContactPage(page)
      setClientError(null)
      setPageError(pageErr)
      if (!pageErr && !emailErr) onSubmit(null, email.trim() || null, name.trim() || null, cleanContactPage(page))
      else document.querySelector(pageErr ? "[data-contact-page-fields] input" : "#creator-email")?.focus()
      return
    }
    const err = clientValidate(value)
    setClientError(err)
    if (!err && !emailErr) onSubmit(normalizeDestinationUrl(value), email.trim() || null, name.trim() || null)
    // The submit can sit far below the field (the dashboard's finish bar), so
    // take the person to the field that needs fixing.
    else document.getElementById(err ? "destination-url" : "creator-email")?.focus()
  }

  // A server-side email rejection lands on the email field, and a bot-layer
  // refusal lands on the form; neither belongs on the URL field. Everything
  // else — including a field name this form has no slot for — falls through to
  // the URL field, which is the only place it can be *seen*. Routing on "has a
  // field" rather than on the fields we actually render would swallow it.
  const serverField = serverError && serverError.field
  const emailServerError = serverField === "email" ? serverError.message : null
  const formServerError = serverField === "form" ? serverError.message : null
  // A refused page comes back keyed by field (Api::V1::ContactPagesController
  // .invalid_payload); the client's own check shows until the server answers.
  const pageServerErrors = serverField === "contact_page" ? contactPageErrorsFromServer(serverError.details, serverError.message) : null
  const pageErrorsShown = (submitted && pageError) || pageServerErrors
  const urlServerError = HANDLED_SERVER_FIELDS.includes(serverField)
    ? null
    : (serverError && serverError.message !== undefined ? serverError.message : serverError)

  // A destination-integrity refusal carries a re-check link for the URL slot.
  const urlAppealUrl = urlServerError && serverError?.appealUrl
  const error = (submitted && clientError) || urlServerError
  const suggestion = submitted && clientError ? mailtoSuggestion(normalizeDestinationUrl(value)) : null
  const emailErrorShown = (submitted && emailError) || emailServerError
  const dashboard = layout === "dashboard"
  // Sending, or a press held for the browser check: nothing on the form moves.
  const locked = disabled || held

  const emailEl = requireEmail && (
    <div>
      <label htmlFor="creator-email" className="block text-sm font-semibold mb-2 text-coal dark:text-cloud">
        Your email
      </label>
      <input
        id="creator-email"
        name="email"
        type="email"
        autoComplete="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => {
          const next = e.target.value
          setEmail(next)
          if (submitted) setEmailError(validateEmail(next))
        }}
        disabled={locked}
        aria-invalid={Boolean(emailErrorShown)}
        aria-describedby={emailErrorShown ? "creator-email-error" : "creator-email-hint"}
        className={fieldClasses(emailErrorShown)}
      />
      {emailErrorShown
        ? (
          <p id="creator-email-error" className="mt-2 text-sm text-[color:var(--color-fail)]">
            {emailErrorShown}
          </p>
        )
        : (
          /* The notice is what makes PECR soft opt-in valid for creators who
             never make an account: they are told at collection time what they
             are getting and that they can leave. Without this line the
             non-account half of the marketing list has no lawful basis, so
             don't quietly trim it for layout. See
             docs/specs/2026-08-31-lifecycle-email-campaigns.md. */
          <p id="creator-email-hint" className="mt-2 text-sm text-[color:var(--color-muted)] dark:text-muted-light">
            We'll send your code here plus occasional TangoQR updates. Opt out
            anytime.{" "}
            <a href="/privacy" className="underline hover:text-voltage-dark dark:hover:text-voltage-light">
              Privacy
            </a>
          </p>
        )}
    </div>
  )

  // The kind picker keeps the scheme out of the text field; the text field is
  // deliberately type="text" (not "url"): WebKit treats type="url" inputs as
  // autofillable contact fields and, once a visitor has any saved tangoqr.com
  // credential, pops the Apple Passwords / iCloud Keychain AutoFill key on the
  // first such field on load. The form is noValidate with a client-side
  // clientValidate, so no native url validation is lost.
  const inputEl = (
    <DestinationInput
      id="destination-url"
      value={value}
      placeholder={destinationPlaceholder}
      onChange={(next) => {
        setValue(next)
        if (submitted) setClientError(clientValidate(next))
      }}
      disabled={locked}
      contactPages={contactPages}
      hosted={hosted}
      onHostedChange={(next) => { setHosted(next); setPageError(null) }}
      pickerClassName={fieldClasses(error && !hosted).replace("w-full ", "")}
      inputClassName={fieldClasses(error)}
      inputProps={{
        name: "destination_url",
        // This is a destination field, never a credential. Browsers and
        // password managers ignore autoComplete="off" and still pop a fill, so
        // opt out explicitly per manager. data-* cover 1Password, LastPass,
        // Bitwarden, and Dashlane; Apple Passwords / iCloud Keychain ignores
        // all of those and is handled by the type="text" choice above.
        "data-1p-ignore": "",
        "data-lpignore": "true",
        "data-bwignore": "",
        "data-form-type": "other",
        "aria-invalid": Boolean(error),
        "aria-describedby": error ? "destination-url-error" : undefined,
      }}
    />
  )

  const errorEl = error && (
    <p id="destination-url-error" className="mt-2 text-sm text-[color:var(--color-fail)]">
      {error}
      {error === urlServerError && urlAppealUrl && <> <ReCheckLink href={urlAppealUrl} /></>}
      <MailtoSuggestion
        suggestion={suggestion}
        onApply={(next) => { setValue(next); setClientError(null) }}
      />
    </p>
  )

  // The code's name (#326), optional. Without one a code is named from its
  // link (QrCode#display_label), which reads the same for every code an
  // operator points at their own site. Never printed on the image, so no
  // character rule beyond the length. Not a person's name either: the
  // password-manager opt-outs match the destination field's, so "Name" doesn't
  // pull up a contact card.
  const nameEl = (
    <div className="mt-4">
      <label htmlFor="code-label" className="block text-sm font-semibold mb-2 text-coal dark:text-cloud">
        Name <span className="font-normal text-[color:var(--color-muted)] dark:text-muted-light">(optional)</span>
      </label>
      <input
        id="code-label"
        name="label"
        type="text"
        autoComplete="off"
        maxLength={NAME_MAX_LENGTH}
        placeholder="e.g. Truck A window"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={disabled}
        aria-describedby="code-label-hint"
        data-1p-ignore=""
        data-lpignore="true"
        data-bwignore=""
        data-form-type="other"
        className={fieldClasses(false)}
      />
      <p id="code-label-hint" className="mt-2 text-sm text-[color:var(--color-muted)] dark:text-muted-light">
        Where will this code go? Only you see the name. It isn't printed on the code.
      </p>
    </div>
  )

  // Form-level, because a bot-layer refusal is not the fault of either field.
  // It carries an escape hatch: Turnstile has a real false-positive rate on
  // humans, this is the primary conversion action, and a dead end here just
  // loses the visitor. role="alert" so a screen reader announces it without
  // the focus having moved. Issue #166.
  const formErrorEl = formServerError && (
    <div
      role="alert"
      className="mt-4 rounded border border-[color:var(--color-fail)] px-4 py-3 text-sm text-[color:var(--color-fail)]"
    >
      {formServerError}{" "}
      <a href="/contact" className="underline hover:no-underline">
        Still stuck? Get in touch
      </a>{" "}
      and we'll send your code.
    </div>
  )

  // 52px and 17px on a phone in the hero (the mobile design handoff's primary
  // CTA), settling back to the shared 44px control height from `sm:` up and in
  // the dashboard, where the button sits inline in a row of 44px controls.
  const submitClasses =
    "rounded-lg bg-voltage text-coal font-semibold shadow-voltage transition " +
    "hover:bg-voltage-dark disabled:opacity-60 disabled:cursor-not-allowed " +
    (dashboard ? "min-h-[44px]" : "min-h-[52px] text-[17px] sm:min-h-[44px] sm:text-base")

  // The hero's button carries a forward arrow to read as the funnel's first
  // step; the dashboard's is a plain button.
  const arrow = !dashboard

  // One submit button across all four arms (hero/dashboard × gated/ungated) —
  // the copy, the disabled state, and the icon have a single definition, so the
  // gated and ungated arms of the same form can't drift. Only width and
  // position fork: ungated it sits inline at the end of the input row; gated it
  // drops full-width below the stacked destination + email.
  const submitEl = (
    <button
      type="submit"
      disabled={locked}
      className={
        (requireEmail
          ? "w-full mt-4 "
          : (dashboard ? "w-full sm:w-auto " : "w-full min-[620px]:w-auto ")) +
        "px-5 " +
        (arrow ? "inline-flex items-center justify-center gap-2 " : "") +
        submitClasses
      }
    >
      {disabled ? "Generating…" : verifying || held ? "Checking your browser…" : arrow ? (
        <>
          Generate my QR
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
          </svg>
        </>
      ) : "Generate my QR"}
    </button>
  )

  // Both layouts: visible label, input + right-sized button on one row, which
  // stacks full-width on narrow viewports (~640px dashboard, ~620px hero).
  // A hosted contact page (#332): the picker stays where the URL field was and
  // the page's fields open below it. The submit then sits under them, never
  // beside the picker.
  const pageFieldsEl = hosted && (
    <div className="mt-4">
      <ContactPageFields
        page={page}
        onChange={(next) => {
          setPage(next)
          if (submitted) setPageError(validateContactPage(next))
        }}
        errors={pageErrorsShown}
        disabled={locked}
        gateEmail={requireEmail && email.trim() ? email.trim() : null}
      />
    </div>
  )

  return (
    <form id={formId} onSubmit={handleSubmit} noValidate className="w-full">
      {hosted ? (
        <span className="block text-sm font-semibold mb-2 text-coal dark:text-cloud">Destination</span>
      ) : (
        <label htmlFor="destination-url" className="block text-sm font-semibold mb-2 text-coal dark:text-cloud">
          Destination
        </label>
      )}
      <div className={"flex flex-col gap-3 " + (dashboard ? "sm:flex-row" : "min-[620px]:flex-row")}>
        <div className="flex-1 min-w-0">{inputEl}</div>
        {!requireEmail && !hideSubmit && !hosted && submitEl}
      </div>
      {pageFieldsEl}
      {errorEl}
      {nameEl}
      {/* Gated: destination and email stack, one submit under both — the cost
          is stated up front rather than sprung after the code is generated. */}
      {requireEmail && (
        <>
          <div className="mt-4">{emailEl}</div>
          {submitEl}
        </>
      )}
      {!requireEmail && !hideSubmit && hosted && <div className="mt-4">{submitEl}</div>}
      {formErrorEl}
    </form>
  )
}
